import { apiClient } from '@/api/generated/client'
import { track } from '@/utils/analytics'
import { makeAutoObservable } from 'mobx'

import { RequestError } from '@/api/http'
import { getStoreProvider } from '@/utils/mobx/getStoreProvider'

/** Role is the qualifying answer; team size is a band, never a number. */
/**
 * Who is asking. One list for both surfaces that ask — the landing's demo form and the onboarding's
 * last step — because two role vocabularies would drift and the answers would stop being comparable.
 *
 * Ordered company-side first, then the people who sell their own time, then capital: a select is read
 * top-down, and grouping by kind beats guessing at frequency. `other` stays last and stays present —
 * without it the field forces a wrong answer, which is worse than no answer.
 *
 * The server stores this as free text (`routes/demo.ts` only requires it to be non-empty), so adding
 * a value here costs nothing downstream and old rows keep the value they were saved with.
 */
export const ROLES = [
  'founder',
  'exec',
  'sales',
  'salesrep',
  'bizdev',
  'partnerships',
  'marketing',
  'recruiting',
  'agency',
  'freelance',
  'investor',
  'other',
] as const
export const TEAM_SIZES = ['solo', '2to5', '6to20', '21to50', 'over50'] as const

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/

type DemoStoreProps = {
  /** Which button opened the form — recorded on the row, so the page can be read as a funnel. */
  source: string
  /**
   * Человек уже в продукте, и его имя и почта известны (спека): формы нет, сразу календарь с ними
   * (Дима, 2026-10-14, «Not sure? Book a demo» в тарифах и Billing). Заявка на сервер при этом не уходит
   * и автописьмо не шлётся: человек ещё ничего не просил, он открыл календарь; встречу записывает Calendly.
   */
  direct?: { name: string; email: string } | null
}

/**
 * The landing's only form: four fields that matter, because every extra one costs a lead.
 *
 * The two checks here exist to give a fast, specific answer — not to protect the server, which
 * repeats both. A browser check only stops people who use a browser.
 */
export class DemoStore {
  name = ''
  role = ''
  email = ''
  company = ''
  teamSize = ''
  note = ''
  /** The honeypot. Invisible to people, irresistible to bots, and never touched by this code. */
  websiteUrl = ''

  sending = false
  sent = false
  error: 'email' | 'role' | 'server' | null = null
  serverMessage = ''

  constructor(private props: DemoStoreProps) {
    if (props.direct) {
      this.name = props.direct.name
      this.email = props.direct.email
    }
    makeAutoObservable(this)
  }

  /** На месте формы календарь: заявка отправлена или форма не нужна вовсе (`direct`). */
  get booking() {
    return this.sent || Boolean(this.props.direct)
  }

  /** Календарь открыт без заявки — подпись над ним другая: «Request received» тут было бы неправдой. */
  get direct() {
    return !this.sent && Boolean(this.props.direct)
  }

  setField = <K extends 'name' | 'role' | 'email' | 'company' | 'teamSize' | 'note' | 'websiteUrl'>(
    field: K,
    value: string,
  ) => {
    this[field] = value
    if ((field === 'email' && this.error === 'email') || (field === 'role' && this.error === 'role')) {
      this.error = null
    }
  }

  submit = async (): Promise<void> => {
    if (this.sending || this.sent) return

    if (!EMAIL.test(this.email.trim())) {
      this.error = 'email'
      return
    }
    if (!this.role) {
      this.error = 'role'
      return
    }

    this.error = null
    this.sending = true
    try {
      await apiClient.demo.requestDemo({
        name: this.name.trim(),
        role: this.role,
        email: this.email.trim(),
        company: this.company.trim(),
        team_size: this.teamSize,
        note: this.note.trim(),
        website_url: this.websiteUrl,
        source: this.props.source,
      })
      this.sent = true
      // Category fields only — the name, email and note stay between us and the CRM.
      track('demo_requested', { role: this.role, team_size: this.teamSize || null })
    } catch (error) {
      // The server's own words when it has any: "a valid work email is required" is more use than
      // "something went wrong".
      this.error = 'server'
      this.serverMessage = error instanceof RequestError ? error.message : String(error)
    } finally {
      this.sending = false
    }
  }
}

export const { StoreProvider: DemoProvider, useStore: useDemoStore } = getStoreProvider(DemoStore)
